import React, { useState } from 'react';
import {
  Drawer,
  InputGroup,
  FormGroup,
  TextArea,
  Callout,
  Intent,
  Position,
  Button,
  Tooltip,
} from '@blueprintjs/core';
import styled from 'styled-components';
import colors from 'utils/colors';
import copy from 'copy-to-clipboard';
import { IconNames } from '@blueprintjs/icons';
import {
  DrawerHeader,
  PlainButton,
  FlexRow,
  PaddingWrapper,
} from 'components/styled-components';
import { Formik, Form, ErrorMessage, Field } from 'formik';
import ErrorWrapper from 'components/forms/components/error-wrapper';
import * as Yup from 'yup';
import join from 'lodash/join';
import { updateEmailTemplate } from 'api/email';
import { AppToaster } from 'components/AppToaster';
import shortid from 'shortid';
import { DialogWrapper } from 'components/DialogWrapper';

const ValidationSchema = Yup.object().shape({
  type: Yup.string()
    .min(5, 'Too Short!')
    .required('Required'),
  subject: Yup.string()
    .min(5, 'Too Short!')
    .required('Required'),
  message: Yup.string()
    .min(10, 'Too Short!')
    .required('Required'),
});

const EditDrawer = ({ template, toggleOpen, isOpen, fetch }) => {
  const [editing, toggleEditing] = useState(false);
  const [isDialogOpen, toggleDialog] = useState(false);

  const handleClose = reset => {
    toggleEditing(false);
    toggleOpen(false);
    reset();
  };

  const handleBeforeSubmit = () => {
    toggleDialog(true);
  };

  const submit = (values, actions) => {
    actions.setSubmitting(true);
    const { subject, message, type } = values;
    updateEmailTemplate({ emailType: type, subject, message })
      .then(res => {
        AppToaster.show({
          message: res.data.message,
          intent: Intent.SUCCESS,
        });
        actions.setSubmitting(false);
        handleClose(fetch);
      })
      .catch(err => {
        AppToaster.show({
          message: err.response.data.message,
          intent: Intent.DANGER,
        });
      });
  };

  const validateMessage = value => {
    const requiredFields = template.dynamicParams.map(p => `{{${p.name}}}`);
    const missingParams = requiredFields.filter(
      field => value.indexOf(field) === -1
    );
    const isValid = requiredFields.every(field => value.indexOf(field) !== -1);

    return isValid
      ? null
      : `Missing Value Placeholders: ${join(missingParams, ', ')}`;
  };

  const copyToClipboard = text => {
    copy(text);
    AppToaster.show({
      message: `Copied '${text}' to Clipboard!`,
      intent: Intent.PRIMARY,
    });
  };

  return (
    <Formik
      initialValues={{
        type: template.type,
        subject: template.subject,
        message: template.message,
      }}
      onSubmit={handleBeforeSubmit}
      validationSchema={ValidationSchema}
      validateOnBlur={true}
    >
      {({ handleChange, values, handleReset, isSubmitting, ...rest }) => (
        <Drawer
          style={{ overflow: 'auto' }}
          isOpen={isOpen}
          onClose={() => handleClose(handleReset)}
          lazy
          usePortal
        >
          <DialogWrapper
            isOpen={isDialogOpen}
            onClose={() => toggleDialog(false)}
            title="Publish Changes?"
            body="These changes will be made public. Are you sure you wish to publish these changes?"
            confirm="YES"
            action={() => submit(values, rest)}
          />
          <Form>
            <DrawerHeader>
              <FlexRow>
                <span>Email Template</span>
                {editing ? (
                  <div>
                    <Button
                      text="cancel"
                      minimal
                      onClick={() => {
                        handleReset();
                        toggleEditing(false);
                      }}
                    />
                    <PlainButton
                      width="8rem"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      SAVE
                    </PlainButton>
                  </div>
                ) : (
                  <PlainButton width="8rem" onClick={toggleEditing}>
                    EDIT
                  </PlainButton>
                )}
              </FlexRow>
            </DrawerHeader>
            <PaddingWrapper>
              <FormGroup label="Type" labelFor="type-input">
                <ErrorMessage
                  name="type"
                  render={msg => <ErrorWrapper>{msg}</ErrorWrapper>}
                />
                <InputGroup
                  large
                  id="type-input"
                  name="type"
                  value={values.type}
                  onChange={handleChange}
                  disabled
                  maxLength={100}
                  minLength={1}
                />
              </FormGroup>
              <FormGroup label="Subject" labelFor="subject-input">
                <ErrorMessage
                  name="subject"
                  render={msg => <ErrorWrapper>{msg}</ErrorWrapper>}
                />
                <InputGroup
                  large
                  id="subject-input"
                  name="subject"
                  value={values.subject}
                  onChange={handleChange}
                  disabled={!editing}
                  maxLength={100}
                  minLength={1}
                />
              </FormGroup>
              <Field
                name="message"
                validate={validateMessage}
                render={() => (
                  <FormGroup
                    label="Message"
                    labelFor="message-input"
                    style={{ minHeight: 300 }}
                  >
                    <ErrorMessage
                      name="message"
                      render={msg => <ErrorWrapper>{msg}</ErrorWrapper>}
                    />
                    <TextArea
                      fill
                      large
                      name="message"
                      id="message-input"
                      value={values.message}
                      onChange={handleChange}
                      disabled={!editing}
                      style={{
                        height: '20rem',
                        minHeight: '4rem',
                        resize: 'vertical',
                      }}
                    />
                  </FormGroup>
                )}
              />

              <Callout intent={Intent.PRIMARY} icon={IconNames.ISSUE}>
                <p style={{ marginBottom: '1rem' }}>
                  These double curly brace values are placeholders for the
                  personalizable portions of the email. Removing them from the
                  template will omit them from the email.{' '}
                  <strong>They must each be used once or more</strong>, and each
                  template has it's own set of variables that can't be shared.
                  Click one to copy it to your clipboard!
                </p>
                {template.dynamicParams.map(field => (
                  <EmailCodeWrapper key={shortid.generate()}>
                    <Tooltip
                      content={<span>Click to Copy!</span>}
                      position={Position.LEFT}
                    >
                      <CopyableText
                        onClick={() => copyToClipboard(`{{${field.name}}}`)}
                      >{`{{${field.name}}}`}</CopyableText>
                    </Tooltip>
                    <NonSelectableText>{` - ${field.desc}`}</NonSelectableText>
                  </EmailCodeWrapper>
                ))}
              </Callout>
            </PaddingWrapper>
          </Form>
        </Drawer>
      )}
    </Formik>
  );
};

const EmailCodeWrapper = styled.div`
  margin-bottom: 0.5rem;
`;

const CopyableText = styled.span`
  background-color: ${colors.whiteGray};
  border-radius: 5px;
  padding: 0.25rem;

  &:hover {
    cursor: pointer;
    background-color: ${colors.white};
  }
`;

const NonSelectableText = styled.span`
  user-select: none;
  -moz-user-select: none;
  -khtml-user-select: none;
  -webkit-user-select: none;
  -o-user-select: none;
`;

export default EditDrawer;
